import { useCallback, useRef } from "react";
import { CARD_BASE } from "../homeTokens";

/**
 * One editorial card: copy on the left, artwork on the right.
 *
 * The pointer tilt is applied imperatively rather than through React state —
 * `mousemove` fires far too often to round-trip through a render, and the
 * transform never needs to survive a re-render. The inner `[data-zoom]` layer
 * counter-parallaxes so the artwork feels like it sits behind the card face
 * instead of painted on it.
 *
 * When a real cover asset exists it wins; the abstract placeholder is the
 * fallback. That inversion is the whole fix for the old dashboard, where every
 * card fell back to the same grey gradient and the row read as three identical
 * blanks.
 */
export default function EditorialCard({ item, onOpen, size = "regular" }) {
  const cardRef = useRef(null);

  const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const handleMouseMove = useCallback((event) => {
    const el = cardRef.current;
    if (!el || prefersReducedMotion()) return;
    const rect = el.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `rotateY(${px * 4.5}deg) rotateX(${-py * 4.5}deg) translateY(-4px)`;
    el.style.boxShadow = "0 28px 56px rgba(20,22,45,.22)";
    el.querySelectorAll("[data-zoom]").forEach((layer) => {
      layer.style.transform = `scale(1.06) translate(${-px * 8}px,${-py * 8}px)`;
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    const el = cardRef.current;
    if (!el) return;
    el.style.transform = "rotateY(0deg) rotateX(0deg) translateY(0)";
    el.style.boxShadow = "none";
    el.querySelectorAll("[data-zoom]").forEach((layer) => {
      layer.style.transform = "scale(1)";
    });
  }, []);

  const titleSize =
    size === "hero" ? "text-xl" : size === "large" ? "text-[19px]" : "text-[17px]";
  const minHeight = size === "regular" ? "min-h-[156px]" : "min-h-[168px]";

  return (
    <button
      ref={cardRef}
      type="button"
      onClick={() => onOpen?.(item)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`${CARD_BASE} ${minHeight} w-full border border-black/[.06] text-left dark:border-transparent`}
      style={{ background: item.background }}
    >
      <div className="flex w-[52%] min-w-0 flex-none flex-col py-[22px] pl-6 pr-2">
        <div className="font-mono text-[9.5px] font-medium uppercase leading-none tracking-[.14em] text-[#6c6f80] dark:text-white/[.42]">
          {item.kicker}
        </div>
        <div
          className={`mt-2.5 ${titleSize} font-bold leading-[1.24] tracking-[-.024em] text-[#15161f] dark:text-white`}
        >
          {item.title}
        </div>
        <span className="mt-auto inline-flex h-[33px] flex-none self-start items-center whitespace-nowrap rounded-[17px] bg-[#15161f] px-4 text-xs font-bold text-white dark:bg-white dark:text-[#0a0a0f]">
          {item.cta}
        </span>
      </div>

      <div className="relative min-w-0 flex-1 overflow-hidden">
        <div
          data-zoom="1"
          className="absolute -bottom-6 -right-[34px] left-2 top-[26px] transition-transform duration-[550ms] ease-[cubic-bezier(.2,.7,.3,1)]"
        >
          {item.coverUrl ? (
            <img
              src={item.coverUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full rounded-[13px] border border-black/[.08] object-cover shadow-[0_18px_40px_rgba(20,22,45,.18)] dark:border-white/[.13] dark:shadow-[0_18px_40px_rgba(0,0,0,.35)]"
            />
          ) : (
            <div className="absolute inset-0 rounded-[13px] border border-black/[.06] bg-[linear-gradient(150deg,rgba(255,255,255,.9),rgba(255,255,255,.55))] p-4 shadow-[0_18px_40px_rgba(20,22,45,.14)] dark:border-white/[.13] dark:bg-[linear-gradient(150deg,rgba(255,255,255,.17),rgba(255,255,255,.045))] dark:shadow-[0_18px_40px_rgba(0,0,0,.35)]">
              <div className="flex items-center gap-[7px]">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: item.accent }}
                />
                <span className="h-1.5 w-2/5 rounded-sm bg-black/20 dark:bg-white/30" />
              </div>
              <div className="mt-3 h-1.5 w-[84%] rounded-sm bg-black/[.10] dark:bg-white/[.15]" />
              <div className="mt-1.5 h-1.5 w-[66%] rounded-sm bg-black/[.10] dark:bg-white/[.15]" />
              <div className="mt-1.5 h-1.5 w-[76%] rounded-sm bg-black/[.06] dark:bg-white/[.09]" />
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
